import { Box, Button, Flex, Heading, HStack, Input, Switch, Text, VStack } from '@chakra-ui/react'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import type { ExportOptions } from './exportTypes'

// Re-export for backward compatibility
export type PdfExportOptions = ExportOptions

interface ExportPdfDialogProps {
  customerName?: string
  hasCustomerLogo: boolean
  onClose: () => void
  onExport: (options: PdfExportOptions) => void
  isExporting: boolean
}

function ExportPdfDialog({
  customerName,
  hasCustomerLogo,
  onClose,
  onExport,
  isExporting,
}: ExportPdfDialogProps) {
  const [title, setTitle] = useState('HiveMQ Solution Architecture')
  const [seName, setSeName] = useState('')
  const [seEmail, setSeEmail] = useState('')
  const [aeName, setAeName] = useState('')
  const [aeEmail, setAeEmail] = useState('')
  const [includeCustomerBranding, setIncludeCustomerBranding] = useState(true)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onExport({
      title: title.trim() || 'HiveMQ Solution Architecture',
      seName: seName.trim(),
      seEmail: seEmail.trim(),
      aeName: aeName.trim(),
      aeEmail: aeEmail.trim(),
      includeCustomerBranding,
    })
  }

  const hasCustomerInfo = customerName || hasCustomerLogo

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <Heading size="md">Export as PDF</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} disabled={isExporting}>
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              {/* Document Title */}
              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Document Title
                </Text>
                <Input
                  placeholder="HiveMQ Solution Architecture"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </Box>

              {/* SE Contact Info */}
              <Box bg="bg.subtle" p={4} borderRadius="md">
                <Text fontWeight="medium" mb={3} fontSize="sm">
                  Solutions Engineer (SE)
                </Text>
                <VStack gap={3}>
                  <Input
                    placeholder="Name"
                    value={seName}
                    onChange={(e) => setSeName(e.target.value)}
                    size="sm"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={seEmail}
                    onChange={(e) => setSeEmail(e.target.value)}
                    size="sm"
                  />
                </VStack>
              </Box>

              {/* AE Contact Info */}
              <Box bg="bg.subtle" p={4} borderRadius="md">
                <Text fontWeight="medium" mb={3} fontSize="sm">
                  Account Executive (AE)
                </Text>
                <VStack gap={3}>
                  <Input
                    placeholder="Name"
                    value={aeName}
                    onChange={(e) => setAeName(e.target.value)}
                    size="sm"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={aeEmail}
                    onChange={(e) => setAeEmail(e.target.value)}
                    size="sm"
                  />
                </VStack>
              </Box>

              {/* Customer Branding Toggle */}
              {hasCustomerInfo && (
                <HStack justify="space-between">
                  <Box>
                    <Text fontWeight="medium" fontSize="sm">
                      Include Customer Branding
                    </Text>
                    <Text fontSize="xs" color="content.secondary">
                      {customerName ? `Show "${customerName}"` : 'Show customer logo'}
                      {customerName && hasCustomerLogo ? ' and logo' : ''}
                    </Text>
                  </Box>
                  <Switch.Root
                    checked={includeCustomerBranding}
                    onCheckedChange={(e) => setIncludeCustomerBranding(e.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                </HStack>
              )}

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose} disabled={isExporting}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="brand" loading={isExporting}>
                  Export PDF
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

export default ExportPdfDialog
